<template>
  <div
    class="challenge-header"
    :style="{
      background: `linear-gradient(-180deg, rgba(26, 32, 44, 0) 0%, rgba(26, 32, 44, 0.85) 100%), url('${backgroundUrl}') center`,
    }"
  >
    <div class="challenge-header__row">
      <div class="challenge-header__tags">
        <StatusChip :entity="challenge" />
        <v-chip small class="ms-2 white--text blue-grey darken-2">
          {{ revs }}
        </v-chip>
      </div>

      <div class="d-flex">
        <v-btn icon class="mr-2" v-if="canEdit" @click="optionClicked('edit')">
          <v-icon color="white" size="20">
            mdi-pencil-outline
          </v-icon>
        </v-btn>

        <ContextualMenu
          dotsColor="white"
          data-testid="challengeHeader-contextualMenu"
          data-tourid="challengeView-button-contextualMenu"
          :options="options"
          @optionClicked="optionClicked"
        />
      </div>
    </div>
    <div class="challenge-header__row d-flex">
      <div class="challenge-header__data">
        <p class="challenge-header__date" v-if="isChallengeActive">
          {{ date }}
        </p>
        <p
          class="challenge-header__name"
          data-testid="challengeHeader-text-challengeName"
          data-private
        >
          {{ challengeName }}
        </p>
      </div>
      <CreateRevButton
        :challengeId="challengeId"
        :disabled="!isChallengeActive"
      />
    </div>

    <ChallengeModal
      :show="showChallengeModal"
      :challenge="challenge"
      @close="showChallengeModal = false"
    />
    <DeleteConfirmationModal
      :show="showDeleteModal"
      :isLoading="isDeleting"
      :title="$t('challenge.remove_this_challenge')"
      :text="
        $t('challenge.you_are_about_to_remove_the_challenge', { challengeName })
      "
      @cancel="showDeleteModal = false"
      @delete="deleteChallenge"
    />
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import _ from 'lodash'

import mixin from '@/mixins/mixin'

import ContextualMenu from '@/components/Core/ContextualMenu.vue'
import ChallengeModal from '@/components/ChallengeModal/ChallengeModal.vue'
import DeleteConfirmationModal from '@/components/Modals/DeleteConfirmationModal.vue'
import CreateRevButton from './CreateRevButton.vue'
import StatusChip from '@/components/Core/StatusChip.vue'

export default {
  name: 'ChallengeViewHeader',

  props: {
    challenge: {
      type: Object,
      required: true,
    },
    date: {
      type: String,
      required: false,
    },
    revs: {
      type: String,
      required: false,
    },
    challengeId: {
      type: String,
      required: false,
    },
    backgroundUrl: {
      type: String,
      required: false,
    },
    isChallengeActive: {
      type: Boolean,
      required: false,
    },
    canEdit: {
      type: Boolean,
      required: false,
    },
  },

  components: {
    ContextualMenu,
    CreateRevButton,
    ChallengeModal,
    DeleteConfirmationModal,
    StatusChip,
  },

  mixins: [mixin],

  data() {
    return {
      showChallengeModal: false,
      showDeleteModal: false,
      isDeleting: false,
    }
  },

  computed: {
    organization() {
      return this.getOrganization()
    },

    challengeName() {
      return this.challenge?.name
    },

    options() {
      return _.compact([
        {
          tid: 'challenge_share',
          id: 'share',
          text: this.$t('common.button.share'),
          icon: 'mdi-share-variant',
        },
        this.canEdit && {
          tid: 'challenge_delete',
          id: 'delete',
          text: this.$t('common.button.delete'),
          icon: 'mdi-delete-outline',
        },
      ])
    },
  },

  methods: {
    ...mapActions({
      showModal: 'modals/showModal',
    }),
    optionClicked(id) {
      if (id === 'edit') {
        this.showChallengeModal = true
      } else if (id === 'delete') {
        this.showDeleteModal = true
      } else if (id === 'share') {
        const owner = this.getAccount()

        this.showModal({
          type: 'shareModal',
          props: {
            name: this.challengeName,
            shareType: 'Challenge',
            title: this.$t('common.share.challenge'),
            organizationId: this.organization._id,
            entityId: this.challengeId,
            sharingAccountId: owner.id,
          },
        })
      }
    },

    hideModal() {
      this.showDeleteModal = false
    },

    deleteChallenge: function() {
      this.isDeleting = true
      this.$store
        .dispatch('removeChallenge', {
          organizationId: this.organization._id,
          challengeId: this.challengeId,
        })
        .then(() => {
          this.showSuccess(this.$t('challenge.challenge_successfully_deleted'))
          this.isDeleting = false
          this.hideModal()
          this.navigateTo({
            name: 'explore',
            query: {
              types: ['challenge'],
            },
          })
        })
        .catch(() => {
          this.showError(
            this.$t(
              'error.challenge.the_challenge_cannot_be_deleted_because_it_already_has_revs'
            )
          )
          this.isDeleting = false
          this.hideModal()
        })
    },
  },
}
</script>

<style lang="scss" scoped>
.challenge-header {
  padding: 20px;
  border-radius: 4px;
  p {
    margin: 0;
  }
  background-size: cover !important;
}
.challenge-header__name {
  color: $color-white;
  font-style: normal;
  font-weight: 600;
  font-size: 20px;
  line-height: 24px;
  display: flex;
  align-items: center;
  letter-spacing: 0.25px;
  margin: 0;
  word-break: break-word;
}
.challenge-header__date {
  font-style: normal;
  font-weight: 500;
  font-size: 12px;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: #cbd5e0;
  margin-bottom: 10px;
}
.challenge-header__status {
  border-radius: 4px 0px 0px 4px;
  text-transform: capitalize;
}

.challenge-header__status,
.challenge-header__row {
  display: flex;
  justify-content: space-between;
  #create-rev {
    margin: 0;
  }
}
.challenge-header__data {
  padding-right: 10px;
}
.challenge-header__create-rev {
  margin: 0;
  align-self: flex-end;
}

.header-title__challenge {
  display: flex;
}
.challenge-header__tags {
  display: flex;
  margin-bottom: 70px;
}
.endingSoon {
  background: $color-orange;
}
.active {
  background: $color-blue-darken1;
}
.closed {
  background: $color-green;
}
</style>
